import { Injectable, signal } from '@angular/core';

/**
 * Interface for component state setters used by event handlers
 */
export interface ComponentStateSetters {
  setStartDateValue: (value: Date | null) => void;
  setEndDateValue: (value: Date | null) => void;
  setBookingValue: (value: string | null) => void;
  setParkingPlaceValue: (value: string | null) => void;
  setDisabledParkingPlaceValue: (value: boolean) => void;
  setPaidValue: (value: boolean) => void;
  setLicensePlateNotKnownValue: (value: boolean) => void;
  setLicensePlateValue: (value: string) => void;
  setLicensePlateCountryCodeValue: (value: string | null) => void;
}

/**
 * Interface for component state getters used by event handlers
 */
export interface ComponentStateGetters {
  getBookingValue: () => string | null;
  getLicensePlateNotKnownValue: () => boolean;
}

/**
 * Interface for component callbacks used by event handlers
 */
export interface ComponentCallbacks {
  loadBookingForParkingRequest: (bookingIri: string) => void;
  clearLicensePlateFields: () => void;
  updateDateConstraints: (minDate: Date | null, maxDate: Date | null) => void;
  onBookingSearchChange: (event: any) => void;
}

/**
 * Service to handle all form event handlers for parking request forms
 */
@Injectable()
export class ParkingRequestEventHandlerService {
  /**
   * Handle start date change event
   */
  onStartDateChange(date: Date | null, setters: ComponentStateSetters): void {
    setters.setStartDateValue(date);
  }

  /**
   * Handle end date change event
   */
  onEndDateChange(date: Date | null, setters: ComponentStateSetters): void {
    setters.setEndDateValue(date);
  }

  /**
   * Handle booking change event
   */
  onBookingChange(bookingIri: string | null, setters: ComponentStateSetters, callbacks: ComponentCallbacks): void {
    setters.setBookingValue(bookingIri);

    if (bookingIri) {
      callbacks.loadBookingForParkingRequest(bookingIri);
    } else {
      // Clear date constraints when no booking is selected
      callbacks.updateDateConstraints(null, null);
    }
  }

  /**
   * Handle parking place change event
   */
  onParkingPlaceChange(parkingPlaceIri: string | null, setters: ComponentStateSetters): void {
    setters.setParkingPlaceValue(parkingPlaceIri);
  }

  /**
   * Handle disabled parking place change event
   */
  onDisabledParkingPlaceChange(value: boolean, setters: ComponentStateSetters): void {
    setters.setDisabledParkingPlaceValue(value);
  }

  /**
   * Handle paid change event
   */
  onPaidChange(value: boolean, setters: ComponentStateSetters): void {
    setters.setPaidValue(value);
  }

  /**
   * Handle license plate not known change event
   */
  onLicensePlateNotKnownChange(value: boolean, setters: ComponentStateSetters, callbacks: ComponentCallbacks): void {
    setters.setLicensePlateNotKnownValue(value);

    if (value) {
      callbacks.clearLicensePlateFields();
    }
  }

  /**
   * Handle license plate change event
   */
  onLicensePlateChange(value: string, setters: ComponentStateSetters): void {
    setters.setLicensePlateValue(value);
  }

  /**
   * Handle license plate country code change event
   */
  onLicensePlateCountryCodeChange(value: string | null, setters: ComponentStateSetters): void {
    setters.setLicensePlateCountryCodeValue(value);
  }

  /**
   * Handle booking search change event
   */
  onBookingSearchChange(event: any, callbacks: ComponentCallbacks): void {
    callbacks.onBookingSearchChange(event);
  }
}
